import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Home, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CommentaryPicker } from "./CommentaryPanel";

/* Breadcrumb for commentary pages: [commentary ▾] › Home › [book ▾] › chapter. The book crumb is a dropdown of every
   book this commentary covers, so readers can jump across the commentary without leaving the page. */
export function CommentaryBreadcrumb({ slug, book, chapter }) {
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ["commentary-books", slug], queryFn: async () => (await api.get(`/commentaries/${slug}`)).data, staleTime: 60 * 60 * 1000 });
  const books = data?.books || [];
  const Sep = () => <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" aria-hidden="true" />;
  return (
    <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5" data-testid="commentary-breadcrumb">
      <CommentaryPicker slug={slug} onChange={(v) => navigate(chapter ? `/commentary/${v}/${encodeURIComponent(book)}/${chapter}` : book ? `/commentary/${v}/${encodeURIComponent(book)}` : `/commentary/${v}`)} testId="commentary-switch-select" />
      <Sep />
      <Link to={`/commentary/${slug}`} className="inline-flex h-8 items-center gap-1.5 rounded-full border border-border bg-background px-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground transition-colors hover:border-gold hover:text-foreground" data-testid="commentary-home-crumb" title={`All books in ${data?.commentary?.name || "this commentary"}`}>
        <Home className="h-3.5 w-3.5 text-gold" /> All books
      </Link>
      {book && (
        <>
          <Sep />
          <Select value={book} onValueChange={(b) => navigate(`/commentary/${slug}/${encodeURIComponent(b)}`)}>
            <SelectTrigger className="h-8 w-auto min-w-[150px] rounded-full bg-background font-serif text-sm" data-testid="commentary-book-crumb" aria-label="Jump to another book"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-[60vh]">
              {["OT", "NT"].map((t) => {
                const list = books.filter((b) => b.testament === t);
                return list.length ? [<div key={t} className="px-2 pb-1 pt-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{t === "OT" ? "Old Testament" : "New Testament"}</div>, ...list.map((b) => <SelectItem key={b.book} value={b.book}>{b.book}</SelectItem>)] : null;
              })}
              {!books.some((b) => b.book === book) && <SelectItem value={book}>{book}</SelectItem>}
            </SelectContent>
          </Select>
        </>
      )}
      {chapter && (
        <>
          <Sep />
          <span className="font-mono text-[11px] uppercase tracking-wider text-gold" data-testid="commentary-chapter-crumb">Chapter {chapter}</span>
        </>
      )}
    </nav>
  );
}
